import { useEffect, useMemo, useState } from 'react';
import { getTimelineTrackSpriteFrames } from '../utils/timelineSpriteCache';

const DEFAULT_FPS = 25;
const MIN_FRAME_COUNT = 3;
const MAX_FRAME_COUNT = 18;

const isVideoLikeUrl = (url = '') => {
  if (!url || typeof url !== 'string') return false;
  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.startsWith('blob:') ||
    lowerUrl.startsWith('data:video') ||
    /\.(mp4|webm|mov|m4v|mkv|ogg)(\?|#|$)/i.test(lowerUrl)
  );
};

const isAudioLikeUrl = (url = '') => {
  if (!url || typeof url !== 'string') return false;
  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.startsWith('data:audio') ||
    /\.(mp3|wav|ogg|m4a|aac|flac)(\?|#|$)/i.test(lowerUrl)
  );
};

const isImageLikeUrl = (url = '') => {
  if (!url || typeof url !== 'string') return false;
  const lowerUrl = url.toLowerCase();
  return (
    lowerUrl.startsWith('data:image') ||
    /\.(png|jpe?g|webp|gif|bmp|svg)(\?|#|$)/i.test(lowerUrl)
  );
};

const firstDefined = (...values) => values.find((value) => !!value) || null;

const resolveTrackMediaSource = (element) => {
  const componentData = element?.componentData || {};
  const baseType = (element?.type || componentData.type || '').toLowerCase();
  const mediaType = baseType === 'ai'
    ? (componentData.aiMediaType || 'image').toLowerCase()
    : baseType;

  if (mediaType === 'text') {
    return null;
  }

  if (mediaType === 'video') {
    const sourceUrl = firstDefined(
      isVideoLikeUrl(componentData.url) ? componentData.url : null,
      isVideoLikeUrl(componentData.currSrc) ? componentData.currSrc : null,
      isVideoLikeUrl(componentData.preview) ? componentData.preview : null,
      componentData.url,
      componentData.currSrc,
      componentData.preview
    );

    const posterUrl = firstDefined(
      isImageLikeUrl(componentData.thumbnail) ? componentData.thumbnail : null,
      isImageLikeUrl(componentData.preview) ? componentData.preview : null,
      isImageLikeUrl(componentData.url) ? componentData.url : null
    );

    return {
      mediaType: 'video',
      sourceUrl,
      posterUrl,
    };
  }

  if (mediaType === 'audio') {
    return {
      mediaType: 'audio',
      sourceUrl: firstDefined(componentData.url, componentData.preview),
      posterUrl: firstDefined(
        isImageLikeUrl(componentData.thumbnail) ? componentData.thumbnail : null,
        isImageLikeUrl(componentData.preview) ? componentData.preview : null
      ),
    };
  }

  if (mediaType === 'gif') {
    return {
      mediaType: 'gif',
      sourceUrl: firstDefined(componentData.preview, componentData.url, componentData.thumbnail),
      posterUrl: firstDefined(componentData.thumbnail, componentData.preview, componentData.url),
    };
  }

  if (mediaType === 'image') {
    return {
      mediaType: 'image',
      sourceUrl: firstDefined(componentData.preview, componentData.thumbnail, componentData.url),
      posterUrl: firstDefined(componentData.thumbnail, componentData.preview, componentData.url),
    };
  }

  const fallbackUrl = firstDefined(
    componentData.url,
    componentData.currSrc,
    componentData.preview,
    componentData.thumbnail
  );

  if (!fallbackUrl) return null;

  if (isVideoLikeUrl(fallbackUrl)) {
    return {
      mediaType: 'video',
      sourceUrl: fallbackUrl,
      posterUrl: firstDefined(componentData.thumbnail, componentData.preview),
    };
  }

  if (isAudioLikeUrl(fallbackUrl)) {
    return {
      mediaType: 'audio',
      sourceUrl: fallbackUrl,
      posterUrl: firstDefined(componentData.thumbnail, componentData.preview),
    };
  }

  return {
    mediaType: 'image',
    sourceUrl: fallbackUrl,
    posterUrl: firstDefined(componentData.thumbnail, componentData.preview, fallbackUrl),
  };
};

export const useTimelineTrackSprites = ({
  element,
  clipDurationSec = 1,
  frameCount = 6,
  fps = DEFAULT_FPS,
  enabled = true,
}) => {
  const [spriteFrames, setSpriteFrames] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const mediaInfo = useMemo(() => resolveTrackMediaSource(element), [element]);

  const startTimeSec = useMemo(() => {
    const croppedInPoint = element?.timeline?.croppedInPoint || 0;
    return Math.max(0, croppedInPoint / fps);
  }, [element?.timeline?.croppedInPoint, fps]);

  const durationSec = useMemo(() => {
    const safe = Number.isFinite(clipDurationSec) ? clipDurationSec : 1;
    return Math.max(0.1, safe);
  }, [clipDurationSec]);

  const safeFrameCount = useMemo(() => {
    const preferred = Number.isFinite(frameCount) ? frameCount : 6;
    return Math.max(MIN_FRAME_COUNT, Math.min(MAX_FRAME_COUNT, Math.round(preferred)));
  }, [frameCount]);

  const requestSignature = useMemo(() => {
    if (!enabled || (!mediaInfo?.sourceUrl && !mediaInfo?.posterUrl)) {
      return '';
    }

    return [
      mediaInfo.mediaType,
      mediaInfo.sourceUrl || '',
      mediaInfo.posterUrl || '',
      startTimeSec.toFixed(3),
      durationSec.toFixed(3),
      safeFrameCount,
    ].join('|');
  }, [enabled, mediaInfo, startTimeSec, durationSec, safeFrameCount]);

  useEffect(() => {
    let cancelled = false;

    if (!requestSignature || !mediaInfo) {
      setSpriteFrames([]);
      setIsLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setIsLoading(true);

    getTimelineTrackSpriteFrames({
      mediaType: mediaInfo.mediaType,
      sourceUrl: mediaInfo.sourceUrl,
      posterUrl: mediaInfo.posterUrl,
      startTimeSec,
      durationSec,
      frameCount: safeFrameCount,
    })
      .then((frames) => {
        if (cancelled) return;
        setSpriteFrames(Array.isArray(frames) ? frames : []);
        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        console.warn('Unable to build timeline track sprites:', error);
        setSpriteFrames([]);
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [requestSignature, mediaInfo, startTimeSec, durationSec, safeFrameCount]);

  return {
    spriteFrames,
    isLoading,
    mediaType: mediaInfo?.mediaType || null,
  };
};
